import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  EditorCaret,
  EditorChange,
  insertRuleChange,
  toEditorLines,
  toggleListChange
} from 'src/helpers/descriptionEditorCommands'
import {
  caretFromSelection,
  editorDomMatches,
  lineRowFromNode,
  paintLines,
  readEditorDom
} from 'src/helpers/descriptionEditorDom'
import {
  caretFromHistory,
  caretIndex,
  isRedoKey,
  isUndoKey,
  useDescriptionHistory
} from 'src/helpers/descriptionEditorHistory'
import { caretForRewrite, commandLineIds, editForKey, pasteEditor } from 'src/helpers/descriptionEditorInput'
import { parseMobileDescription, runLength, serializeMobileDescription } from 'src/helpers/mobileDescription'
import { fieldBoxShadow } from '../atoms/FormTextInput'
import DescriptionSurface from './DescriptionSurface'
import DescriptionToolbar from './DescriptionToolbar'
import { useEditorPlacement } from './useEditorPlacement'
import { useFocusedLine } from './useFocusedLine'

interface MobileDescriptionEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  error?: boolean
}

const MobileDescriptionEditor = ({
  value,
  onChange,
  placeholder,
  error = false
}: MobileDescriptionEditorProps): JSX.Element => {
  const [lines, setLines] = useState(() => toEditorLines(parseMobileDescription(value)))
  const [domVersion, setDomVersion] = useState(0)
  const linesRef = useRef(lines)
  const valueRef = useRef(value)
  const caretRef = useRef<EditorCaret | null>(null)
  const suspendSync = useRef(false)
  const ownChange = useRef(false)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const { focusedId, focusedIdRef } = useFocusedLine(surfaceRef)
  linesRef.current = lines
  valueRef.current = value

  const readSnapshot = useCallback(() => {
    const root = surfaceRef.current
    return {
      value: valueRef.current,
      caret: root === null ? null : caretIndex(linesRef.current, caretFromSelection(root))
    }
  }, [])
  const history = useDescriptionHistory(readSnapshot)
  const { remember, commit, undo: undoSnapshot, redo: redoSnapshot, forget, clear, canUndo, canRedo } = history

  const publish = useCallback(
    (change: EditorChange) => {
      linesRef.current = change.lines
      setLines(change.lines)
      caretRef.current = change.caret
      if (change.structural) {
        setDomVersion((version) => version + 1)
      }
      const serialized = serializeMobileDescription(change.lines)
      const previous = valueRef.current
      commit(previous, serialized)
      if (serialized !== previous) {
        valueRef.current = serialized
        onChange(serialized)
      }
    },
    [commit, onChange]
  )

  const restore = useCallback(
    (snapshot: ReturnType<typeof undoSnapshot>) => {
      if (snapshot === null) {
        return
      }
      const next = toEditorLines(parseMobileDescription(snapshot.value))
      linesRef.current = next
      setLines(next)
      caretRef.current = caretFromHistory(next, snapshot.caret)
      setDomVersion((version) => version + 1)
      if (snapshot.value !== valueRef.current) {
        ownChange.current = serializeMobileDescription(next) !== snapshot.value
        valueRef.current = snapshot.value
        onChange(snapshot.value)
      }
    },
    [onChange]
  )
  const undo = useCallback(() => {
    restore(undoSnapshot())
  }, [restore, undoSnapshot])
  const redo = useCallback(() => {
    restore(redoSnapshot())
  }, [restore, redoSnapshot])

  const syncFromDom = useCallback(() => {
    if (suspendSync.current) {
      return
    }
    const root = surfaceRef.current
    if (root === null) {
      return
    }
    const next = readEditorDom(root)
    const rewrite = !editorDomMatches(root, next)
    publish({ lines: next, caret: rewrite ? caretForRewrite(root, next) : null, structural: rewrite })
  }, [publish])

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const root = surfaceRef.current
      if (root === null) {
        return
      }
      if (isUndoKey(event)) {
        event.preventDefault()
        undo()
        return
      }
      if (isRedoKey(event)) {
        event.preventDefault()
        redo()
        return
      }
      if (event.key === 'Enter' || event.key === 'Backspace' || event.key === 'Delete') {
        remember()
      }
      suspendSync.current = true
      const change = editForKey(root, event)
      suspendSync.current = false
      if (change === null) {
        return
      }
      event.preventDefault()
      publish(change)
    },
    [publish, remember, undo, redo]
  )

  const onPaste = useCallback(
    (event: ClipboardEvent) => {
      const root = surfaceRef.current
      if (root === null) {
        return
      }
      event.preventDefault()
      const clipboard = event.clipboardData?.getData('text/plain') ?? ''
      if (clipboard.length === 0) {
        return
      }
      remember()
      suspendSync.current = true
      const result = pasteEditor(root, clipboard)
      suspendSync.current = false
      if (result.change !== null) {
        publish(result.change)
        return
      }
      if (result.sync) {
        syncFromDom()
      }
    },
    [publish, remember, syncFromDom]
  )

  useEffect(() => {
    const root = surfaceRef.current
    if (root !== null) {
      root.addEventListener('beforeinput', remember)
      root.addEventListener('input', syncFromDom)
      root.addEventListener('keydown', onKeyDown)
      root.addEventListener('paste', onPaste)
    }
    return () => {
      if (root === null) {
        return
      }
      root.removeEventListener('beforeinput', remember)
      root.removeEventListener('input', syncFromDom)
      root.removeEventListener('keydown', onKeyDown)
      root.removeEventListener('paste', onPaste)
    }
  }, [onKeyDown, onPaste, remember, syncFromDom])

  useEffect(() => {
    if (serializeMobileDescription(linesRef.current) === value) {
      return
    }
    if (ownChange.current) {
      ownChange.current = false
      return
    }
    clear()
    const next = toEditorLines(parseMobileDescription(value))
    linesRef.current = next
    setLines(next)
    caretRef.current = null
    setDomVersion((version) => version + 1)
  }, [clear, value])

  useEditorPlacement({ rootRef: surfaceRef, linesRef, caretRef, domVersion, placeholder })

  const applyBold = (): void => {
    const root = surfaceRef.current
    if (root === null) {
      return
    }
    remember()
    const anchor = window.getSelection()?.anchorNode ?? null
    const inside = anchor !== null && root.contains(anchor)
    if (!inside) {
      root.focus()
    }
    document.execCommand('styleWithCSS', false, 'false')
    document.execCommand('bold')
    syncFromDom()
  }

  const toggleList = (): void => {
    const root = surfaceRef.current
    if (root === null) {
      return
    }
    remember()
    const change = toggleListChange(linesRef.current, commandLineIds(root, focusedIdRef.current))
    if (change === null) {
      forget()
      return
    }
    publish({ ...change, structural: !paintLines(root, change.lines) })
  }

  const insertRule = (): void => {
    const root = surfaceRef.current
    const selection = window.getSelection()
    const fromSelection = root === null ? '' : lineRowFromNode(root, selection?.focusNode ?? null)?.dataset.lineId ?? ''
    remember()
    const id = fromSelection === '' ? focusedIdRef.current : fromSelection
    publish(insertRuleChange(linesRef.current, id))
  }

  const ids = surfaceRef.current === null ? [] : commandLineIds(surfaceRef.current, focusedId)
  const chosen = lines.filter((line) => ids.includes(line.id) && line.kind !== 'rule')
  const listOn = chosen.length > 0 && chosen.every((line) => line.kind === 'bullet')
  const showPlaceholder = lines.length === 1 && lines[0].kind === 'text' && runLength(lines[0].runs) === 0

  return (
    <div
      className="self-stretch overflow-hidden rounded-md bg-backgrounds-bg-field-component"
      style={{ boxShadow: fieldBoxShadow(error) }}>
      <DescriptionToolbar
        listOn={listOn}
        canUndo={canUndo}
        canRedo={canRedo}
        onBold={applyBold}
        onList={toggleList}
        onRule={insertRule}
        onUndo={undo}
        onRedo={redo}
      />
      <div className="relative max-h-80 overflow-y-auto">
        {showPlaceholder && (
          <div className="pointer-events-none absolute left-4 top-2 text-sm font-medium font-Montserrat leading-5 text-foregrounds-fg-muted">
            {placeholder}
          </div>
        )}
        <DescriptionSurface ref={surfaceRef} />
      </div>
    </div>
  )
}

export default MobileDescriptionEditor
